import React, { useEffect, useState } from 'react';
import { Card, Layout, Table, Tag, Typography, message, Button, Space, Modal, Popconfirm, Select } from 'antd';
import { MailOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { collection, getDocs, orderBy, query, where, getDoc, doc, runTransaction, deleteField } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import dayjs from 'dayjs';
import apis from '../../apis';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatRupiah = (amount) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

const PublicCustomersList = () => {
    const { currentEventId } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [venuesMap, setVenuesMap] = useState({});
    const [loading, setLoading] = useState(false);

    // Seat Assignment State
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [seatLayout, setSeatLayout] = useState([]);
    const [loadingSeats, setLoadingSeats] = useState(false);
    const [selectedNewSeats, setSelectedNewSeats] = useState([]);
    const [quantityToAssign, setQuantityToAssign] = useState(0);

    const [eventsList, setEventsList] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');

    useEffect(() => {
        if (currentEventId && !selectedEventId) {
            setSelectedEventId(currentEventId);
        }
    }, [currentEventId, selectedEventId]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const eventsSnap = await getDocs(collection(db, 'events'));
                const eventsData = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                eventsData.sort((a, b) => b.id.localeCompare(a.id));
                setEventsList(eventsData);
            } catch (error) {
                console.error("Error fetching events list:", error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (selectedEventId) {
            fetchCustomers();
        }
    }, [selectedEventId]);

    const fetchCustomers = async () => {
        if (!selectedEventId) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'publicBookings'), 
                where('eventId', '==', selectedEventId)
            );
            const querySnapshot = await getDocs(q);
            let data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by createdAt desc in memory to avoid requiring a composite index in Firestore
            data.sort((a, b) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
                return timeB - timeA;
            });

            const eventRef = doc(db, 'events', selectedEventId);
            const eventSnap = await getDoc(eventRef);
            if (eventSnap.exists()) {
                const eventData = eventSnap.data();
                if (eventData.venues) {
                    const vMap = {};
                    eventData.venues.forEach(v => { vMap[v.id] = v.label; });
                    setVenuesMap(vMap);
                }
            }

            setCustomers(data);
        } catch (error) {
            console.error('Error fetching customers:', error);
            message.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const openAssignModal = async (record) => {
        const perfTickets = (record.tickets || [])
            .filter(t => t.id !== 'master_class')
            .reduce((acc, t) => acc + t.quantity, 0);
        const assignedCount = (record.selectedSeatIds || []).length;
        const toAssign = perfTickets - assignedCount;

        if (toAssign <= 0) {
            message.info('All seats are already assigned for this booking.');
            return;
        }

        setSelectedBooking(record);
        setQuantityToAssign(toAssign);
        setSelectedNewSeats([]);
        setIsAssignModalOpen(true);
        setLoadingSeats(true);
        setSeatLayout([]);

        try {
            const sessionId = `${record.date}_${record.session}`;
            const seatsCollection = collection(db, `seats${selectedEventId || 'APCS2026'}`);
            const q = query(seatsCollection, where('sessionId', '==', sessionId), where('venueId', '==', record.venue));
            const snapshot = await getDocs(q);
            
            const fetchedSeats = [];
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                if (data.status === 'locked' && data.lockedAt) {
                    const lockedAtTime = data.lockedAt.toDate ? data.lockedAt.toDate().getTime() : data.lockedAt;
                    if (Date.now() - lockedAtTime > 30 * 60 * 1000) {
                        data.status = 'available'; // lock expired
                    }
                }
                fetchedSeats.push({ id: docSnap.id, ...data });
            });

            // Format layout
            const groupedByArea = fetchedSeats.reduce((acc, seat) => {
                const area = seat.areaType?.toLowerCase();
                if (!acc[area]) acc[area] = [];
                acc[area].push(seat);
                return acc;
            }, {});

            const finalLayouts = {};
            const areaOrder = ['presto', 'allegro', 'lento'];
            const rightSideMax = {
                A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
                G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
                N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
            };

            const areasInDb = Object.keys(groupedByArea);
            const sortedAreas = areasInDb.sort((a, b) => {
                const idxA = areaOrder.indexOf(a.toLowerCase());
                const idxB = areaOrder.indexOf(b.toLowerCase());
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
            });

            sortedAreas.forEach(area => {
                if (groupedByArea[area]) {
                    const rowsObject = groupedByArea[area].reduce((acc, seat) => {
                        const row = seat.row;
                        if (!acc[row]) acc[row] = [];
                        acc[row].push(seat);
                        return acc;
                    }, {});

                    const sortedRowKeys = Object.keys(rowsObject).sort((a, b) => a.localeCompare(b));

                    finalLayouts[area] = sortedRowKeys.map(rowKey => {
                        const rowSeats = rowsObject[rowKey];
                        rowSeats.sort((a, b) => b.number - a.number);
                        
                        const mappedRow = rowSeats.map(seat => {
                            let tooltipText = `Status: ${seat.status.toUpperCase()}`;
                            if (seat.status === 'booked' && seat.assignedTo) {
                                tooltipText = `Booked by: ${seat.assignedTo.registrantName || seat.assignedTo.userName} (${seat.assignedTo.userEmail || ''})`;
                            }
                            
                            return {
                                id: seat.id,
                                number: seat.number,
                                row: seat.row,
                                isReserved: seat.status !== 'available',
                                status: seat.status,
                                tooltip: (
                                    <div style={{ textAlign: 'center' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>Seat {seat.seatLabel}</strong>
                                        <span>{tooltipText}</span>
                                    </div>
                                ),
                                statusColor: seat.status === 'booked' ? '#ff4d4f' : seat.status === 'reserved' ? '#bfbfbf' : seat.status === 'locked' ? '#faad14' : undefined,
                                seatLabel: seat.seatLabel
                            };
                        });

                        if (record.venue === 'Venue1') {
                            const splitSeatNumber = rightSideMax[rowKey];
                            if (splitSeatNumber) {
                                const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
                                if (splitIndex !== -1) {
                                    mappedRow.splice(splitIndex, 0, null, null);
                                }
                            }
                        }

                        return mappedRow;
                    });
                }
            });

            const finalLayoutArray = [];
            areaOrder.forEach(area => {
                if (finalLayouts[area]) {
                    finalLayoutArray.push(...finalLayouts[area]);
                }
            });

            setSeatLayout(finalLayoutArray);

        } catch (error) {
            console.error("Error fetching seat layout:", error);
            message.error("Failed to load seat map.");
        } finally {
            setLoadingSeats(false);
        }
    };

    const handleSelectSeat = (seat) => {
        setSelectedNewSeats(prev => [...prev, seat]);
    };

    const handleDeselectSeat = (seat) => {
        setSelectedNewSeats(prev => prev.filter(s => s.id !== seat.id));
    };

    const handleAssignSeatsSubmit = async () => {
        if (selectedNewSeats.length !== quantityToAssign) {
            message.error(`Please select exactly ${quantityToAssign} seats.`);
            return;
        }

        setLoading(true);
        try {
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', selectedBooking.id);
                const bookingDoc = await transaction.get(bookingRef);
                if (!bookingDoc.exists()) throw new Error("Booking no longer exists.");

                const bookingData = bookingDoc.data();
                
                // Validate seats
                const seatRefs = selectedNewSeats.map(s => doc(db, `seats${selectedEventId || 'APCS2026'}`, s.id));
                const seatDocs = [];
                for (let ref of seatRefs) {
                    const snap = await transaction.get(ref);
                    seatDocs.push(snap);
                }

                seatDocs.forEach((seatSnap, index) => {
                    if (!seatSnap.exists()) throw new Error(`Seat ${selectedNewSeats[index].id} not found.`);
                    const sData = seatSnap.data();
                    const isAvailable = sData.status === 'available' || (sData.status === 'locked' && sData.lockedAt && (Date.now() - (sData.lockedAt.toDate ? sData.lockedAt.toDate().getTime() : sData.lockedAt) > 30*60*1000));
                    if (!isAvailable) {
                        throw new Error(`Seat ${sData.seatLabel} is no longer available.`);
                    }
                });

                // Update seats
                seatDocs.forEach(seatSnap => {
                    transaction.update(seatSnap.ref, {
                        status: 'booked',
                        bookingId: selectedBooking.id,
                        assignedTo: {
                            registrantName: bookingData.buyerName || bookingData.userName || '',
                            userEmail: bookingData.userEmail || ''
                        },
                        lockedAt: null,
                        lockedByBookingId: null
                    });
                });

                // Update booking
                const newSeatIds = selectedNewSeats.map(s => s.id);
                const newSeatLabels = selectedNewSeats.map(s => s.seatLabel);

                const currentSelectedSeatIds = bookingData.selectedSeatIds || [];
                const currentPerformanceSeatLabels = bookingData.performanceSeatLabels || [];

                transaction.update(bookingRef, {
                    selectedSeatIds: [...currentSelectedSeatIds, ...newSeatIds],
                    performanceSeatLabels: [...currentPerformanceSeatLabels, ...newSeatLabels],
                    seatsSelected: true
                });
            });

            message.success('Seats successfully assigned!');
            setIsAssignModalOpen(false);
            fetchCustomers();
        } catch (error) {
            console.error("Assignment error:", error);
            message.error(`Failed to assign seats: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleResendEmail = async (bookingId) => {
        try {
            message.loading({ content: 'Resending email...', key: 'resendEmail' });
            await apis.publicTicket.resendPublicTicketEmail({ bookingId });
            message.success({ content: 'Email resent successfully!', key: 'resendEmail' });
            
            // Update the local state to show email as sent
            setCustomers(prev => prev.map(c => c.id === bookingId ? { ...c, emailSent: true } : c));
        } catch (error) {
            console.error('Error resending email:', error);
            message.error({ content: error.response?.data?.message || 'Failed to resend email.', key: 'resendEmail' });
        }
    };

    const handleMarkPaid = async (record) => {
        try {
            message.loading({ content: 'Marking as paid...', key: 'markPaid' });
            
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', record.id);
                const bookingDoc = await transaction.get(bookingRef);
                
                if (!bookingDoc.exists()) {
                    throw new Error("Booking does not exist.");
                }

                const bookingData = bookingDoc.data();
                
                // Collect all seat IDs
                const seatIdsToReserve = [
                    ...(bookingData.selectedSeatIds || []),
                    ...(bookingData.orchestraSelectedSeatIds || [])
                ];

                // Retrieve all seat docs
                const seatDocs = [];
                for (let seatId of seatIdsToReserve) {
                    const seatRef = doc(db, `seats${selectedEventId || 'APCS2026'}`, seatId);
                    const seatSnap = await transaction.get(seatRef);
                    if (seatSnap.exists()) {
                        seatDocs.push(seatSnap);
                    }
                }

                // Update seats
                for (let seatSnap of seatDocs) {
                    transaction.update(seatSnap.ref, {
                        status: 'booked',
                        bookingId: record.id,
                        lockedAt: deleteField(),
                        lockedByBookingId: deleteField(),
                        assignedTo: {
                            userName: bookingData.userName || '',
                            userEmail: bookingData.userEmail || '',
                            registrantName: bookingData.registrantName || ''
                        }
                    });
                }

                // Update booking
                transaction.update(bookingRef, {
                    paymentStatus: 'PAID',
                    updatedAt: new Date()
                });
            });

            message.success({ content: 'Marked as PAID successfully! You can now resend the email.', key: 'markPaid' });
            setCustomers(prev => prev.map(c => c.id === record.id ? { ...c, paymentStatus: 'PAID' } : c));
        } catch (error) {
            console.error('Error marking as paid:', error);
            message.error({ content: error.message || 'Failed to mark as paid', key: 'markPaid' });
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        try {
            message.loading({ content: 'Deleting booking...', key: 'deleteBooking' });
            await runTransaction(db, async (transaction) => {
                const bookingRef = doc(db, 'publicBookings', bookingId);
                const bookingDoc = await transaction.get(bookingRef);
                
                if (!bookingDoc.exists()) {
                    throw new Error("Booking does not exist.");
                }

                const bookingData = bookingDoc.data();
                
                // Collect all seat IDs
                const seatIdsToFree = [
                    ...(bookingData.selectedSeatIds || []),
                    ...(bookingData.orchestraSelectedSeatIds || [])
                ];

                // Retrieve all seat docs
                const seatDocs = [];
                for (let seatId of seatIdsToFree) {
                    const seatRef = doc(db, `seats${selectedEventId || 'APCS2026'}`, seatId);
                    const seatSnap = await transaction.get(seatRef);
                    if (seatSnap.exists()) {
                        seatDocs.push(seatSnap);
                    }
                }

                // Update seats to available
                seatDocs.forEach(seatSnap => {
                    const sData = seatSnap.data();
                    // Only free the seat if it's currently tied to THIS booking
                    if (sData.bookingId === bookingId || sData.lockedByBookingId === bookingId) {
                        transaction.update(seatSnap.ref, {
                            status: 'available',
                            bookingId: null,
                            assignedTo: null,
                            lockedAt: null,
                            lockedByBookingId: null
                        });
                    }
                });

                // Delete the booking
                transaction.delete(bookingRef);
            });

            message.success({ content: 'Booking deleted successfully', key: 'deleteBooking' });
            fetchCustomers();
        } catch (error) {
            console.error('Error deleting booking:', error);
            message.error({ content: error.message || 'Failed to delete booking', key: 'deleteBooking' });
        }
    };

    const columns = [
        {
            title: 'Booking ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'userName',
            key: 'userName',
        },
        {
            title: 'Email',
            dataIndex: 'userEmail',
            key: 'userEmail',
        },
        {
            title: 'Phone',
            dataIndex: 'userPhone',
            key: 'userPhone',
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            render: (venue) => venuesMap[venue] || 'APCS Venue', 
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Session',
            dataIndex: 'session',
            key: 'session',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount) => formatRupiah(amount),
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => {
                let color = 'default';
                if (status === 'PAID' || status === 'paid') color = 'green';
                else if (status === 'pending') color = 'orange';
                else if (status === 'expired') color = 'red';
                
                return (
                    <Tag color={color}>
                        {status ? status.toUpperCase() : 'UNKNOWN'}
                    </Tag>
                );
            },
        },
        {
            title: 'Seats',
            dataIndex: 'selectedSeatIds',
            key: 'selectedSeatIds',
            render: (seats) => (seats && seats.length > 0) ? seats.map(s => s.split('_')[0]).join(', ') : '-',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (timestamp) => timestamp ? dayjs(timestamp.toDate()).format('DD MMM YYYY HH:mm') : '-',
        },
        {
            title: 'Email Status',
            dataIndex: 'emailSent',
            key: 'emailSent',
            render: (emailSent) => {
                if (emailSent) return <Tag color="green">SENT</Tag>;
                return <Tag color="red">PENDING/FAILED</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="primary" 
                        icon={<MailOutlined />} 
                        size="small" 
                        onClick={() => handleResendEmail(record.id)}
                        disabled={record.paymentStatus !== 'PAID' && record.paymentStatus !== 'paid'}
                    >
                        Resend
                    </Button>
                    {record.paymentStatus !== 'PAID' && record.paymentStatus !== 'paid' && (
                        <Popconfirm
                            title="Mark as Paid?"
                            description="This will confirm the booking and assign the seats. Continue?"
                            onConfirm={() => handleMarkPaid(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                type="default" 
                                icon={<CheckCircleOutlined />} 
                                size="small" 
                                style={{ color: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Mark Paid
                            </Button>
                        </Popconfirm>
                    )}
                    <Popconfirm
                        title="Delete the booking?"
                        description="Are you sure you want to delete this booking? Associated seats will be freed."
                        onConfirm={() => handleDeleteBooking(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button 
                            danger 
                            icon={<DeleteOutlined />} 
                            size="small"
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    const expandedRowRender = (record) => {
        return (
            <div style={{ margin: '0 24px', padding: '16px', background: '#f9f9f9', borderRadius: '8px' }}>
                <Title level={5} style={{ marginTop: 0 }}>Order Details</Title>
                <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '200px' }}>
                        <Text strong>Tickets:</Text>
                        <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                            {(record.tickets || []).filter(t => t.quantity > 0).map((t, idx) => (
                                <li key={idx}>{t.quantity}x {t.name}</li>
                            ))}
                            {(!record.tickets || record.tickets.filter(t => t.quantity > 0).length === 0) && (
                                <li>None</li>
                            )}
                        </ul>
                    </div>

                    {(record.addOnIds?.length > 0 || record.freeMasterclassCount > 0) && (
                        <div style={{ minWidth: '200px' }}>
                            <Text strong>Add-ons & Extras:</Text>
                            <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                                {(() => {
                                    if (!record.addOnIds) return null;
                                    const counts = {};
                                    record.addOnIds.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
                                    return Object.entries(counts).map(([id, qty]) => (
                                        <li key={id}>{qty}x {id.replace(/_/g, ' ')}</li>
                                    ));
                                })()}
                                {record.freeMasterclassCount > 0 && (
                                    <li>{record.freeMasterclassCount}x Master Class (Free)</li>
                                )}
                            </ul>
                        </div>
                    )}

                    <div style={{ minWidth: '200px' }}>
                        <Text strong>Seats & Assignments:</Text>
                        <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                            <li>
                                <strong>Performance:</strong> {(record.performanceSeatLabels || []).join(', ') || '-'}
                            </li>
                            {(record.orchestraSeatLabels?.length > 0 || record.complimentaryTickets > 0) && (
                                <li>
                                    <strong>Orchestra (Free):</strong> {(record.orchestraSeatLabels || []).join(', ') || 'Auto-Assigned'}
                                </li>
                            )}
                            {record.registrantName && (
                                <li>
                                    <strong>Paying For:</strong> {record.registrantName}
                                </li>
                            )}
                        </ul>
                        {(() => {
                            const perfTickets = (record.tickets || [])
                                .filter(t => t.id?.toLowerCase() !== 'master_class' && t.id?.toLowerCase() !== 'masterclass')
                                .reduce((acc, t) => acc + parseInt(t.quantity || 0, 10), 0);
                            const assignedCount = (record.selectedSeatIds || []).length;
                            const isPaid = record.paymentStatus === 'PAID' || record.paymentStatus === 'paid';
                            
                            if (perfTickets > assignedCount && isPaid) {
                                return (
                                    <Button 
                                        type="primary" 
                                        size="small" 
                                        style={{ marginTop: '16px' }}
                                        onClick={() => openAssignModal(record)}
                                    >
                                        Assign Missing Seats ({perfTickets - assignedCount})
                                    </Button>
                                );
                            } else if (perfTickets > assignedCount && !isPaid) {
                                return (
                                    <div style={{ marginTop: '16px', color: '#faad14' }}>
                                        <i>Cannot assign seats (Payment Status is {record.paymentStatus || 'unknown'})</i>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Content style={{ padding: '24px', minHeight: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>Public Ticket Bookings</Title>
                    <Text type="secondary">List of all public ticket bookings and their payment status for the selected event.</Text>
                </div>
                <div>
                    <span style={{ marginRight: '8px', fontWeight: 500 }}>Filter by Event:</span>
                    <Select 
                        value={selectedEventId} 
                        onChange={(value) => {
                            setSelectedEventId(value);
                            setCustomers([]);
                        }} 
                        style={{ width: 200 }}
                    >
                        {eventsList.map(ev => (
                            <Select.Option key={ev.id} value={ev.id}>{ev.title || ev.id}</Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <Card style={{ marginTop: '24px' }}>
                <Table
                    columns={columns}
                    dataSource={customers}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                    expandable={{ expandedRowRender }}
                />
            </Card>

            <Modal
                title={`Assign Seats for ${selectedBooking?.buyerName || selectedBooking?.userName}`}
                open={isAssignModalOpen}
                onCancel={() => setIsAssignModalOpen(false)}
                width={1000}
                onOk={handleAssignSeatsSubmit}
                okText="Save Assignment"
                confirmLoading={loading}
                okButtonProps={{ disabled: selectedNewSeats.length !== quantityToAssign }}
                destroyOnClose
            >
                <div style={{ marginBottom: '16px' }}>
                    <Text>
                        Please select <strong>{quantityToAssign}</strong> seats. 
                        Currently selected: {selectedNewSeats.length}
                    </Text>
                    {selectedBooking && (
                        <div style={{ marginTop: '8px' }}>
                            <Text type="secondary">
                                Note: This user purchased: {(selectedBooking.tickets || []).filter(t => t.id !== 'master_class' && t.quantity > 0).map(t => `${t.quantity}x ${t.name}`).join(', ')}. 
                                Please select seats corresponding to their purchased tier.
                            </Text>
                        </div>
                    )}
                </div>
                <div style={{ overflowX: 'auto', backgroundColor: '#f0f2f5', padding: '24px', borderRadius: '8px' }}>
                    {loadingSeats ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>Loading Seat Map...</div>
                    ) : (
                        <CustomSeatPicker 
                            layout={seatLayout} 
                            selectedSeats={selectedNewSeats} 
                            onSelect={handleSelectSeat} 
                            onDeselect={handleDeselectSeat} 
                            maxSeats={quantityToAssign} 
                        />
                    )}
                </div>
            </Modal>
        </Content>
    );
};

export default PublicCustomersList;
